/* eslint-disable max-classes-per-file */
import _glob from "glob";
import path from "path";
import { promisify } from "util";

import { Config, JobConfig, mergeConfig, parseConfigFile } from "./config";
import type { Job } from "./job";

const glob = promisify(_glob);

export const rootDir = path.dirname(require.main.filename);

export type GetJobFunction = (name: string) => JobInfo;
export type LoadJobFunction = (type: string) => Job<Config, unknown>;
export type LoadJobFromConfigFunction = (cfg: Config) => Job<Config, unknown>;

export interface JobInfo {
	name: string;
	type: string;
	absolutePath: string;
	defaultConfig?: Config;
	canRun: boolean;
	errors: string[];
}

export async function loadConfigFromCommandLine(
	config: Config,
	cache: JobCache
): Promise<Config> {
	const jobJsonPath = config.job as string;
	if (typeof jobJsonPath === "string" && jobJsonPath.endsWith(".json"))
		config = await parseConfigFile(jobJsonPath);

	return loadConfigWithDefaults(config, cache);
}

export function loadConfigWithDefaults(
	config: Config,
	cache: JobCache
): Config {
	const jobInfo = cache.getJobInfoFromConfig(config);
	return mergeConfig(jobInfo.defaultConfig, config);
}

export class JobCache {
	private jobMap: { [type: string]: JobInfo };
	readonly jobNames: { [name: string]: string | string[] } = {};

	jobs: JobInfo[];

	constructor(jobs: { [type: string]: JobInfo }) {
		this.jobs = [];
		this.jobMap = jobs;

		for (const type of Object.keys(jobs)) {
			const job = jobs[type];
			this.jobs.push(job);

			if (this.jobNames[job.name]) {
				if (Array.isArray(this.jobNames[job.name])) {
					const map = this.jobNames[job.name] as string[];
					map.push(job.type);
				} else {
					const map = this.jobNames[job.name] as string;
					this.jobNames[job.name] = [map, job.type];
				}
			} else {
				this.jobNames[job.name] = job.type;
			}
		}
	}

	getJobInfo(name: string): JobInfo {
		let item: JobInfo = this.jobMap[name];
		if (item) return item;

		const type = this.jobNames[name];
		if (type) {
			if (Array.isArray(type)) {
				throw new TypeError(
					`The job name ${name} is ambiguous and matches any one of the following jobs: ${type
						.map(n => this.jobMap[n].type)
						.join(",")}. Use a more specific type name.`
				);
			}

			item = this.jobMap[type];
		}

		if (item) return item;

		if (!item) {
			throw new Error(
				`Could not find a job with type or name '${name}'. Make sure it exists and the name is correct.`
			);
		}
	}

	getJobInfoFromConfig(cfg: Config): JobInfo {
		const jobConfig = cfg.job as JobConfig;
		let jobType = cfg.job as string;

		if (jobConfig.type) {
			jobType = jobConfig.type;
		}

		return this.getJobInfo(jobType);
	}

	loadJob(name: string): Job<Config, unknown> {
		return loadJobWithDefaults(this.getJobInfo(name));
	}

	loadJobFromConfig(cfg: Config): Job<Config, unknown> {
		const jobConfig = cfg.job as JobConfig;
		let jobType = cfg.job as string;

		if (jobConfig.type) {
			jobType = jobConfig.type;
		}

		return this.loadJob(jobType);
	}
}

export default async function buildCache(
	pattern: string[] | string = ["./jobs/*.js", "./jobs/!(*.d).ts"]
): Promise<JobCache> {
	pattern = Array.isArray(pattern) ? pattern : [pattern];

	const jobs: { [type: string]: JobInfo } = {};

	for (const p of pattern) {
		// eslint-disable-next-line no-await-in-loop
		const files: string[] = await glob(p, {
			root: rootDir,
			cwd: rootDir,
			absolute: true
		});

		files.forEach(f => {
			const name = path.basename(f, path.extname(f));
			const type = path.relative(rootDir, f).replace("\\", "/");

			const jobInfo: JobInfo = {
				name,
				type,
				absolutePath: f,
				canRun: true,
				errors: []
			};

			const job = loadJobWithDefaults(jobInfo);

			if (job) {
				jobInfo.defaultConfig = mergeConfig(job.defaultConfig);
			}

			jobs[type] = jobInfo;
		});
	}

	return new JobCache(jobs);
}

let shouldLoadJob = true;

export function disableJobLoading() {
	shouldLoadJob = false;
}

export function enableJobLoading() {
	shouldLoadJob = true;
}

function loadJobWithDefaults(jobInfo: JobInfo): Job<Config, unknown> {
	if (!shouldLoadJob) return;

	let job: Job<Config, unknown>;

	// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, import/no-dynamic-require, global-require, @typescript-eslint/no-var-requires
	const module = require(jobInfo.absolutePath);

	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	if (module.getItems && module.process) job = module as Job<Config, unknown>;

	// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
	if (module.default?.prototype?.constructor)
		// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
		job = new module.default.prototype.constructor();

	jobInfo.errors = [];
	jobInfo.canRun = true;

	if (!job) {
		jobInfo.canRun = false;
		jobInfo.errors.push("Could not load job");
	}

	if (job && !job.getItems) {
		jobInfo.canRun = false;
		jobInfo.errors.push(`Job does not implement getItems.`);
	}

	if (job && !job.process) {
		jobInfo.canRun = false;
		jobInfo.errors.push(`Job does not implement process.`);
	}

	if (!jobInfo.canRun) {
		job = undefined;
	}

	if (job && !job.getItemId) {
		job.getItemId = (item: unknown): string => {
			if (typeof item === "string") return item;
			if ((item as { id: string }).id) return (item as { id: string }).id;

			return JSON.stringify(item, undefined, 2);
		};
	}

	return job;
}
